<template>
	<div class="cms-accordion-block" :style="containerStyle">
		<div
			v-for="(item, idx) in parsedItems"
			:key="idx"
			class="cms-accordion-block__item"
			:class="{ 'cms-accordion-block__item--open': isOpen(idx), 'cms-accordion-block__item--after-open': idx > 0 && isOpen(idx - 1) }"
			:style="itemStyle"
		>
			<!-- Question / trigger -->
			<button
				class="cms-accordion-block__trigger"
				:style="triggerStyle"
				@click="toggle(idx)"
				:aria-expanded="isOpen(idx) ? 'true' : 'false'"
			>
				<span class="cms-accordion-block__question">{{ item.question }}</span>
				<i
					class="fa cms-accordion-block__chevron"
					:class="isOpen(idx) ? 'fa-chevron-up' : 'fa-chevron-down'"
					:style="isOpen(idx) ? { color: accentColor } : {}"
				></i>
			</button>

			<!-- Answer panel (CSS height transition via class) -->
			<div
				class="cms-accordion-block__body"
				:class="{ 'cms-accordion-block__body--open': isOpen(idx) }"
				:style="bodyStyle"
			>
				<div class="cms-accordion-block__answer" v-html="sanitized(item.answer)"></div>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * AccordionBlock.vue
 * ──────────────────
 * A collapsible FAQ accordion. Each item has a question (title) and an answer
 * (rich-text body). Supports single-expand and multi-expand modes.
 *
 * Outer padding, background and visibility are wrapper traits.
 */
import { sanitizeHtml } from '@/websites/cms/blocks/shared/blockUtils.js';

export default {
	name: 'AccordionBlock',

	props: {
		/** JSON string — array of { question, answer } objects */
		items: { type: String, default: '[]' },

		/** Open the first item by default */
		defaultOpen: { type: [Boolean, String], default: false },

		/** Allow multiple items open simultaneously */
		multiExpand: { type: [Boolean, String], default: false },

		/* ── Colours ── */
		triggerBg:          { type: String, default: '#F4F4F5' },
		triggerColor:       { type: String, default: '#1E2031' },
		triggerBorderColor: { type: String, default: '#DDDEE0' },
		bodyBg:             { type: String, default: '#FFFFFF' },
		bodyColor:          { type: String, default: '#1E2031' },
		accentColor:        { type: String, default: '#FB6A00' },

		/* ── Layout ── */
		borderRadius: { type: String, default: '6px' },
		gap:          { type: String, default: '0.5rem' },

		/** Optional stable key for persisting open/closed state in localStorage */
		storageKey: { type: String, default: '' }
	},

	data: function () {
		return {
			openIndex: -1,
			openSet: {}
		};
	},

	created: function () {
		var restored = this.restoreState();
		if (restored) return;

		if (this.isDefaultOpen) {
			if (this.isMultiExpand) {
				this.$set(this.openSet, 0, true);
			} else {
				this.openIndex = 0;
			}
		}
	},

	computed: {
		isDefaultOpen: function () { return this.defaultOpen === true || this.defaultOpen === 'true'; },
		isMultiExpand: function () { return this.multiExpand === true || this.multiExpand === 'true'; },

		/** Stable key for localStorage. Falls back to a hash of the items JSON. */
		resolvedStorageKey: function () {
			if (this.storageKey) return 'cms_acc_' + this.storageKey;
			var src = this.items || '';
			var hash = 0;
			for (var i = 0; i < src.length; i++) {
				hash = ((hash << 5) - hash) + src.charCodeAt(i);
				hash |= 0;
			}
			return 'cms_acc_' + Math.abs(hash);
		},

		parsedItems: function () {
			try {
				var arr = JSON.parse(this.items);
				return Array.isArray(arr) ? arr : [];
			} catch (e) { return []; }
		},

		containerStyle: function () {
			return { display: 'flex', flexDirection: 'column', gap: '0' };
		},

		itemStyle: function () {
			return {
				borderRadius: this.borderRadius,
				overflow: 'hidden',
				border: '1px solid ' + this.triggerBorderColor
			};
		},

		triggerStyle: function () {
			return { background: this.triggerBg, color: this.triggerColor };
		},

		bodyStyle: function () {
			return { background: this.bodyBg, color: this.bodyColor };
		}
	},

	methods: {
		toggle: function (idx) {
			if (this.isMultiExpand) {
				this.$set(this.openSet, idx, !this.openSet[idx]);
			} else {
				this.openIndex = this.openIndex === idx ? -1 : idx;
			}
			this.saveState();
		},

		isOpen: function (idx) {
			if (this.isMultiExpand) return !!this.openSet[idx];
			return this.openIndex === idx;
		},

		sanitized: function (html) {
			return sanitizeHtml(html || '');
		},

		/** Persist open/closed state to localStorage */
		saveState: function () {
			try {
				var state;
				if (this.isMultiExpand) {
					state = { multi: true, openSet: this.openSet };
				} else {
					state = { multi: false, openIndex: this.openIndex };
				}
				localStorage.setItem(this.resolvedStorageKey, JSON.stringify(state));
			} catch (e) { /* storage full or unavailable */ }
		},

		/** Restore open/closed state from localStorage. Returns true if restored. */
		restoreState: function () {
			try {
				var raw = localStorage.getItem(this.resolvedStorageKey);
				if (!raw) return false;
				var state = JSON.parse(raw);
				if (state.multi && state.openSet) {
					for (var k in state.openSet) {
						this.$set(this.openSet, k, !!state.openSet[k]);
					}
					return true;
				} else if (!state.multi && typeof state.openIndex === 'number') {
					this.openIndex = state.openIndex;
					return true;
				}
			} catch (e) { /* corrupted or unavailable */ }
			return false;
		}
	}
};
</script>

<style scoped>
.cms-accordion-block { width: 100%; max-width: 100%; }

.cms-accordion-block__item { border: 1px solid #DDDEE0; margin-top: 15px; transition: margin-top 0.28s ease; }
.cms-accordion-block__item:first-child { margin-top: 0; }
.cms-accordion-block__item--open { margin-top: 30px; }
.cms-accordion-block__item--open:first-child { margin-top: 0; }
.cms-accordion-block__item--after-open { margin-top: 30px; }

.cms-accordion-block__trigger {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding: 0.9rem 1.25rem;
	border: none;
	cursor: pointer;
	font-size: 1em;
	font-weight: 600;
	text-align: left;
	transition: filter 0.15s;
}

.cms-accordion-block__trigger:hover { filter: brightness(0.96); }

.cms-accordion-block__question { flex: 1; }

.cms-accordion-block__chevron {
	font-size: 0.85em;
	transition: transform 0.2s;
	flex-shrink: 0;
}

/* Slide animation via max-height */
.cms-accordion-block__body {
	max-height: 0;
	overflow: hidden;
	transition: max-height 0.28s ease;
}

.cms-accordion-block__body--open { max-height: 3000px; }

.cms-accordion-block__answer {
	padding: 1rem 1.25rem;
	font-size: 1em;
	line-height: 1.65;
}
</style>
